import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "@/lib/api";
import { Loader, PageHeader, Reveal, Stat } from "@/components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Field } from "@/components/profile/ProfileSections";
import { Medal } from "@/components/Medals";

const WeightDialog = ({ open, onOpenChange, onSaved }) => {
  const [w, setW] = useState({ weight_kg: "", waist_cm: "", hip_cm: "", date: new Date().toISOString().slice(0, 10) });
  const save = async () => {
    if (!w.weight_kg) return toast.error("Indica tu peso");
    const num = (v) => (v === "" ? null : Number(v));
    try {
      await api.post("/progress/weights", { weight_kg: Number(w.weight_kg), waist_cm: num(w.waist_cm), hip_cm: num(w.hip_cm), date: w.date });
      toast.success("Registro guardado");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl sm:max-w-md" data-testid="weight-dialog">
        <DialogHeader><DialogTitle className="font-display">Nuevo registro</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Peso (kg)"><Input type="number" step="0.1" value={w.weight_kg} onChange={(e) => setW({ ...w, weight_kg: e.target.value })} className="h-12 rounded-2xl" data-testid="weight-input" /></Field>
          <Field label="Fecha"><Input type="date" value={w.date} onChange={(e) => setW({ ...w, date: e.target.value })} className="h-12 rounded-2xl" data-testid="weight-date-input" /></Field>
          <Field label="Cintura (cm)"><Input type="number" value={w.waist_cm} onChange={(e) => setW({ ...w, waist_cm: e.target.value })} className="h-12 rounded-2xl" data-testid="waist-input" /></Field>
          <Field label="Cadera (cm)"><Input type="number" value={w.hip_cm} onChange={(e) => setW({ ...w, hip_cm: e.target.value })} className="h-12 rounded-2xl" data-testid="hip-input" /></Field>
        </div>
        <button onClick={save} data-testid="weight-submit-btn" className="mt-2 w-full rounded-full bg-ink py-3.5 font-semibold text-white transition-transform active:scale-95">Guardar</button>
      </DialogContent>
    </Dialog>
  );
};

const Chart = ({ data, dataKey, color, unit, title, testid }) => (
  <div className="card-clay p-5" data-testid={testid}>
    <p className="text-sm font-bold">{title}</p>
    <div className="mt-3 h-56">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ left: -18, right: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
          <XAxis dataKey="date" tickFormatter={fmtDate} fontSize={11} />
          <YAxis domain={["dataMin - 1", "dataMax + 1"]} fontSize={11} />
          <Tooltip labelFormatter={fmtDate} formatter={(v) => [`${v} ${unit}`, title]} />
          <Line type="monotone" dataKey={dataKey} stroke={color} strokeWidth={3} dot={{ r: 3 }} connectNulls />
        </LineChart>
      </ResponsiveContainer>
    </div>
  </div>
);

export default function Progress() {
  const [d, setD] = useState(null);
  const [weights, setWeights] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => {
    api.get("/progress/weights").then((r) => setWeights(r.data));
    api.get("/dashboard").then((r) => setD(r.data));
  };
  useEffect(load, []);
  if (!d || !weights) return <Loader />;
  const first = weights[0]?.weight_kg, last = weights[weights.length - 1]?.weight_kg;
  const del = async (id) => { await api.delete(`/progress/weights/${id}`); load(); };

  return (
    <div className="space-y-5" data-testid="progress-page">
      <PageHeader eyebrow="Progreso" tone="streak" title="Tu evolución" subtitle="Registra tu peso y medidas una vez por semana, a la misma hora.">
        <button onClick={() => setOpen(true)} data-testid="weight-add-btn" className="mt-4 flex items-center gap-2 rounded-full bg-ink px-5 py-2.5 text-sm font-semibold text-white transition-transform active:scale-95"><Plus className="h-4 w-4" /> Registrar peso</button>
      </PageHeader>
      <Reveal className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat tone="streak" label="Peso actual" value={last ?? "-"} unit="kg" testid="progress-current-weight" />
        <Stat tone="streak" label="Cambio total" value={first ? +(last - first).toFixed(1) : 0} unit="kg" testid="progress-weight-change" />
        <Stat tone="streak" label="IMC" value={d.targets.bmi} />
        <Stat tone="streak" label="Sesiones hechas" value={d.stats.sessions_completed} testid="progress-sessions" />
      </Reveal>
      <div className="grid gap-4 lg:grid-cols-2">
        <Chart data={weights} dataKey="weight_kg" color="#F59E0B" unit="kg" title="Peso" testid="progress-weight-chart" />
        {weights.some((w) => w.waist_cm) && <Chart data={weights} dataKey="waist_cm" color="#10B981" unit="cm" title="Cintura" testid="progress-waist-chart" />}
        {d.modules.ailments && d.pain_series.length > 1 && <Chart data={d.pain_series} dataKey="pain" color="#6366F1" unit="/10" title="Dolor" testid="progress-pain-chart" />}
      </div>
      <div className="card-clay p-5 sm:p-6" data-testid="progress-medals">
        <p className="text-sm font-bold">Medallas · racha actual {d.streak.current} días · mejor {d.streak.best}</p>
        <div className="mt-5 grid grid-cols-3 gap-5 sm:grid-cols-7">{d.streak.medals.map((m) => <Medal key={m.id} m={m} />)}</div>
      </div>
      <div className="card-clay overflow-hidden" data-testid="weight-history">
        <p className="px-5 pt-5 text-sm font-bold">Historial</p>
        <ul className="mt-2 divide-y divide-stone-100">
          {[...weights].reverse().map((w) => (
            <li key={w.id} className="flex items-center justify-between px-5 py-3 text-sm">
              <span className="text-stone-600">{fmtDate(w.date)}</span>
              <span className="font-semibold">{w.weight_kg} kg{w.waist_cm ? ` · cintura ${w.waist_cm} cm` : ""}</span>
              <button onClick={() => del(w.id)} data-testid={`weight-delete-${w.id}`} className="rounded-full p-1.5 hover:bg-stone-100"><Trash2 className="h-4 w-4 text-stone-400" /></button>
            </li>
          ))}
        </ul>
      </div>
      <WeightDialog key={String(open)} open={open} onOpenChange={setOpen} onSaved={() => { setOpen(false); load(); }} />
    </div>
  );
}
